import { useContext, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
// @ts-ignore - configured in vite.config.ts via alias
import { authService } from '@/services/authService';
import { AuthContext } from '@/contexts/AuthContext';

export function useAuth() {
  const queryClient = useQueryClient();
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  const { user, token, isAuthenticated, isLoading, setAuthState } = context;

  // Login mutation
  const loginMutation = useMutation({
    mutationFn: (credentials: { email: string; password: string }) =>
      authService.signInWithEmail(credentials.email, credentials.password),
    onSuccess: (response: any) => {
      if (response.user) {
        setAuthState({
          user: {
            id: response.user.id,
            email: response.user.email || '',
            role: response.user.app_metadata?.role || 'user',
            username: response.user.user_metadata?.first_name || '',
            firstName: response.user.user_metadata?.first_name || '',
            lastName: response.user.user_metadata?.last_name || '',
          },
          token: response.session?.access_token || response.access_token,
          isAuthenticated: true,
          isLoading: false,
        });
      }
      queryClient.invalidateQueries({ queryKey: ['auth'] });
    },
    onError: (error: any) => {
      console.error('Login failed:', error);
      setAuthState(prev => ({
        ...prev,
        isAuthenticated: false,
        isLoading: false,
      }));
    },
  });

  // Register mutation
  const registerMutation = useMutation({
    mutationFn: (userData: { username?: string; email: string; password: string }) =>
      authService.signUp(userData.email, userData.password, { first_name: userData.username }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['auth'] });
    },
    onError: (error: any) => {
      console.error('Registration failed:', error);
    },
  });

  // Logout function
  const logout = useCallback(async () => {
    await authService.signOut();
    setAuthState({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
    });
    queryClient.clear();
    window.location.href = '/';
  }, [queryClient, setAuthState]);

  // Demo login mutation
  const demoLoginMutation = useMutation({
    mutationFn: () => authService.demoLogin('ADMIN'),
    onSuccess: (response: any) => {
      if (response.user) {
        setAuthState({
          user: {
            id: response.user.id,
            email: response.user.email,
            username: response.user.user_metadata.first_name,
            role: response.user.app_metadata.role,
            firstName: response.user.user_metadata.first_name,
            lastName: response.user.user_metadata.last_name,
          },
          token: response.access_token,
          isAuthenticated: true,
          isLoading: false,
        });
        localStorage.setItem('jwt_token', response.access_token);
        localStorage.setItem('demo_mode', 'true');
        queryClient.invalidateQueries({ queryKey: ['auth'] });
      }
    },
    onError: (error: any) => {
      console.error('Demo login failed:', error);
    },
  });

  // Login function
  const login = useCallback(async (credentials: { email: string; password: string }) => {
    return loginMutation.mutateAsync(credentials);
  }, [loginMutation]);

  // Demo login function
  const demoLogin = useCallback(async () => {
    return demoLoginMutation.mutateAsync();
  }, [demoLoginMutation]);

  // Register function
  const register = useCallback(async (userData: { username?: string; email: string; password: string }) => {
    return registerMutation.mutateAsync(userData);
  }, [registerMutation]);

  // Social Login mutation
  const socialLoginMutation = useMutation({
    mutationFn: (provider: 'google' | 'github' | 'linkedin') =>
      authService.signInWithProvider(provider),
    onError: (error: any) => {
      console.error('Social login failed:', error);
    },
  });

  const socialLogin = useCallback(async (provider: 'google' | 'github' | 'linkedin') => {
    return socialLoginMutation.mutateAsync(provider);
  }, [socialLoginMutation]);

  return {
    user,
    token,
    isAuthenticated,
    isLoading,
    login,
    demoLogin,
    socialLogin,
    register,
    logout,
    isLoggingIn: loginMutation.isPending,
    isDemoLoggingIn: demoLoginMutation.isPending,
    isSocialLoggingIn: socialLoginMutation.isPending,
    isRegistering: registerMutation.isPending,
    loginError: loginMutation.error,
    demoLoginError: demoLoginMutation.error,
    socialLoginError: socialLoginMutation.error,
    registerError: registerMutation.error,
  };
}
